import {GanttChartXAxis} from '@hconnect/ganttchart/src/modules/GanttChartXAxis'
import {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {DrillingChart} from './Drilling'
import {InstallationChart} from './Installation'

import {ShutdownStatus} from '@refra/modules/common/enums'
import {useKilnSettings} from '@refra/modules/refra-planning/providers'
import {
  HotSpot,
  KilnShutdown,
  MaterialInstallation,
  ShutdownDrillingDTO
} from '@refra/modules/refra-planning/types'

const DEFAULT_STEP_WIDTH = 15
const DEFAULT_Y_AXIS_LABEL_WIDTH = 56

export const KilnShutdownGanttChart = ({
  shutdown,
  materialInstallations,
  drillings,
  materialInstallationsWithComments,
  hotSpots
}: {
  shutdown: KilnShutdown
  materialInstallations?: MaterialInstallation[]
  drillings: ShutdownDrillingDTO[]
  hotSpots: HotSpot[]
  materialInstallationsWithComments: string[]
}) => {
  const {t} = useTranslation()
  const {kilnConfig} = useKilnSettings()
  const kilnConfigLength = kilnConfig.payload.configuration.dimensions.length
  const kilnLength =
    typeof kilnConfigLength === 'number' ? kilnConfigLength : parseInt(kilnConfigLength)

  const xAxis = Array.from({length: kilnLength}, (_, index) => index)
  const ganttContainerRef = useRef<HTMLDivElement>(null)
  const [stepWidth, setStepWidth] = useState(DEFAULT_STEP_WIDTH)
  const canvasWidth = stepWidth * xAxis.length

  const isReadOnly = shutdown.shutdownStatus === ShutdownStatus.Finalized

  useEffect(() => {
    const handleResize = () => {
      if (ganttContainerRef.current) {
        setStepWidth(ganttContainerRef.current.clientWidth / xAxis.length)
      }
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [xAxis.length])

  return (
    <div>
      <div className={'ml-16'}>
        <InstallationChart
          materialInstallations={materialInstallations}
          shutdown={shutdown}
          stepWidth={stepWidth}
          kilnLength={kilnLength}
          xAxis={xAxis}
          ganttContainerRef={ganttContainerRef}
          isReadOnly={isReadOnly}
          materialInstallationsWithComments={materialInstallationsWithComments}
          hotSpots={hotSpots}
        />
        <DrillingChart
          shutdown={shutdown}
          drillings={drillings}
          stepWidth={stepWidth}
          kilnLength={kilnLength}
          xAxis={xAxis}
          ganttContainerRef={ganttContainerRef}
          isReadOnly={isReadOnly}
        />
      </div>
      <GanttChartXAxis
        canvasWidth={canvasWidth}
        kilnLength={kilnLength}
        stepWidth={stepWidth}
        yAxisLabelWidth={DEFAULT_Y_AXIS_LABEL_WIDTH}
        xAxisLabel={t('refraPlanning.xAxisLabel')}
      />
    </div>
  )
}
